import React, { useRef, useState } from 'react';
import useCommandAutocomplete from '../../hooks/useCommandAutocomplete';
import Content from '../Content';
import Floating from '../Floating';
import Embedded from '../Embedded';
import { InputRowComponent } from '../InputRow';
import { useKeyboardHandler } from '../../hooks/useKeyboardHandler';
import useChatbot from 'hooks/useChatbot';

interface ChatbotUIProps {
  loading: boolean;
  loadingMessage: string;
  onHeaderMouseDown?: (e: React.MouseEvent) => void;
  onHeaderHoverChange?: (isHovered: boolean) => void;
  startResize: (e: React.MouseEvent) => void;
  resetWidget?: () => void;
  isInitialState?: boolean;
}

const ChatbotInterface: React.FC<ChatbotUIProps> = ({
  loading,
  loadingMessage,
  onHeaderMouseDown,
  onHeaderHoverChange,
  startResize,
  resetWidget,
  isInitialState,
}) => {
  const {
    showCommandSuggestions,
    commandSuggestions,
    autocompleteSuggestion,
    handleInputChange,
    handleTabPress,
    hideCommandSuggestions,
    selectCommand,
  } = useCommandAutocomplete();

  const { messages, historyMessages, ask, tools, mode, abort } = useChatbot();
  const [question, setQuestion] = useState('');

  const [showMessageHistory, setShowMessageHistory] = useState(false);
  const [selectedHistoryIndex, setSelectedHistoryIndex] = useState(0);

  const inputRef = useRef<HTMLInputElement>(null);

  const handleAsk = () => {
    if (question && !loading) {
      ask(question);
      setQuestion('');
      handleInputChange('', tools);
    }
  };

  const getLastUserMessages = () => {
    const allMessages = [...historyMessages, ...messages];
    const seenQuestions = new Set<string>();

    return allMessages
      .slice()
      .reverse()
      .filter((msg) => {
        if (!msg.question || seenQuestions.has(msg.question)) {
          return false;
        }
        seenQuestions.add(msg.question);
        return true;
      })
      .slice(0, 5);
  };

  const { handleKeyDown } = useKeyboardHandler({
    handleTabPress,
    setQuestion,
    showCommandSuggestions,
    commandSuggestions,
    showMessageHistory,
    setShowMessageHistory,
    hideCommandSuggestions,
    getLastUserMessages,
    selectedHistoryIndex,
    setSelectedHistoryIndex,
    loading,
    question,
    handleAsk,
  });

  const renderContent = () => (
    <>
      <Content
        loading={loading}
        loadingMessage={loadingMessage}
        tools={tools}
        setQuestion={setQuestion}
        handleInputChange={handleInputChange}
        inputRef={inputRef}
        selectCommand={selectCommand}
      />
      <InputRowComponent
        showMessageHistory={showMessageHistory}
        showCommandSuggestions={showCommandSuggestions}
        commandSuggestions={commandSuggestions}
        question={question}
        loading={loading}
        autocompleteSuggestion={autocompleteSuggestion}
        selectedHistoryIndex={selectedHistoryIndex}
        inputRef={inputRef}
        tools={tools}
        getLastUserMessages={getLastUserMessages}
        setQuestion={setQuestion}
        setShowMessageHistory={setShowMessageHistory}
        selectCommand={selectCommand}
        setQuestionFromCommand={setQuestion}
        hideCommandSuggestions={hideCommandSuggestions}
        handleInputChange={handleInputChange}
        handleKeyDown={handleKeyDown}
        handleAsk={handleAsk}
        abort={abort}
      />
    </>
  );

  if (mode === 'floating') {
    return (
      <Floating
        startResize={startResize}
        onHeaderMouseDown={onHeaderMouseDown}
        onHeaderHoverChange={onHeaderHoverChange}
        renderContent={renderContent}
        resetWidget={resetWidget}
        isInitialState={isInitialState}
      />
    );
  }

  if (mode === 'embedded') {
    return <Embedded loading={loading} renderContent={renderContent} />;
  }

  return null;
};

export default ChatbotInterface;
